import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

export default function Cart() {
  const { items, updateQty, removeItem, clearCart, subtotal } = useCart();
  const navigate = useNavigate();
  const shipping = items.length ? 6.99 : 0;

  const handleEmpty = () => {
    if (window.confirm("Remove all items from your cart?")) {
      clearCart();
      toast.success("Cart emptied");
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag className="mx-auto text-zinc-700 mb-6" size={56} />
        <h1 className="font-display text-4xl uppercase tracking-tight">Your Cart Is Empty</h1>
        <p className="text-zinc-400 mt-3">No gear yet — time to fix that.</p>
        <Link to="/shop" className="mt-8 inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">Shop Now <ArrowRight size={18} /></Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12" data-testid="cart-page">
      <h1 className="font-display text-5xl uppercase tracking-tighter mb-6">Your <span className="text-[#FF3B30]">Cart</span></h1>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <Link to="/shop" data-testid="continue-shopping-link" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-300 hover:text-[#FF3B30] transition-colors duration-200">
          <ArrowLeft size={16} /> Continue Shopping
        </Link>
        <button onClick={handleEmpty} data-testid="empty-cart-button" className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-400 font-bold uppercase tracking-widest text-xs px-4 py-2.5 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors duration-200">
          <Trash2 size={14} /> Empty Cart
        </button>
      </div>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 border border-zinc-800">
          {items.map((item) => (
            <div key={item.key} className="flex gap-4 p-4 border-b border-zinc-800 last:border-b-0" data-testid={`cart-item-${item.product_id}`}>
              <img src={item.image} alt={item.name} className="h-24 w-24 object-cover bg-zinc-900 border border-zinc-800" />
              <div className="flex-1">
                <h3 className="font-bold uppercase tracking-tight text-sm">{item.name}</h3>
                {item.size && <p className="text-xs text-zinc-500 uppercase tracking-widest mt-1">Size: {item.size}</p>}
                {item.color && <p className="text-xs text-zinc-500 uppercase tracking-widest mt-0.5">Color: {item.color}</p>}
                <div className="font-display text-lg text-[#FF3B30] mt-1">${item.price.toFixed(2)}</div>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center border border-zinc-700">
                    <button onClick={() => updateQty(item.key, item.quantity - 1)} className="px-3 py-1.5 hover:bg-zinc-800 transition-colors">−</button>
                    <span className="px-4 text-sm font-bold">{item.quantity}</span>
                    <button onClick={() => updateQty(item.key, item.quantity + 1)} className="px-3 py-1.5 hover:bg-zinc-800 transition-colors">+</button>
                  </div>
                  <button onClick={() => removeItem(item.key)} data-testid={`remove-${item.product_id}`} className="text-zinc-500 hover:text-[#FF3B30] transition-colors"><Trash2 size={18} /></button>
                </div>
              </div>
              <div className="font-bold text-right whitespace-nowrap">${(item.price * item.quantity).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="border border-zinc-800 p-6 h-fit bg-zinc-900">
          <h2 className="font-display text-2xl uppercase tracking-tight mb-6">Summary</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span className="text-white">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-zinc-400"><span>Shipping</span><span className="text-white">${shipping.toFixed(2)}</span></div>
            <div className="flex justify-between font-display text-2xl pt-4 border-t border-zinc-800"><span>Total</span><span className="text-[#FF3B30]" data-testid="cart-total">${(subtotal + shipping).toFixed(2)}</span></div>
          </div>
          <button onClick={() => navigate("/checkout")} data-testid="checkout-button" className="w-full mt-6 flex items-center justify-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">
            Checkout <ArrowRight size={18} />
          </button>
          <Link to="/shop" className="block text-center mt-4 text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-white transition-colors">Continue Shopping</Link>
        </div>
      </div>
    </div>
  );
}
